import React from 'react';
import { Search, MessageCircle, Plus, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export const ChatSidebar = ({
  conversaciones,
  searchTerm,
  setSearchTerm,
  selectedConversacion,
  setSelectedConversacion,
  setShowNewChat,
  totalNoLeidos,
  isMobileView,
  setShowChatOnMobile
}) => {
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatConversationDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return format(date, 'HH:mm', { locale: es });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return format(date, 'dd/MM/yy', { locale: es });
    }
  };

  const filteredConversaciones = conversaciones.filter(c =>
    c.nombreContacto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.telefono.includes(searchTerm)
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-semibold">Mensajes</h1>
            {totalNoLeidos > 0 && (
              <Badge variant="destructive" className="rounded-full">
                {totalNoLeidos}
              </Badge>
            )}
          </div>
          <Button size="sm" onClick={() => setShowNewChat(true)} data-testid="new-chat-button">
            <Plus className="h-4 w-4 mr-1" />
            Nuevo
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar conversación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Conversations list */}
      <ScrollArea className="flex-1">
        {filteredConversaciones.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hay conversaciones</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowNewChat(true)}>
              Iniciar conversación
            </Button>
          </div>
        ) : (
          filteredConversaciones.map((conv) => (
            <div
              key={conv.id}
              onClick={() => {
                setSelectedConversacion(conv);
                if (isMobileView) setShowChatOnMobile(true);
              }}
              className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-accent/50 transition-colors border-b ${selectedConversacion?.id === conv.id ? 'bg-accent' : ''
                }`}
              data-testid="conversation-item"
            >
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-primary/10 text-primary">
                  {getInitials(conv.nombreContacto)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 truncate">
                    <span className="font-medium truncate">{conv.nombreContacto}</span>
                    {conv.whatsappBloqueado && (
                      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" title="Número penalizado temporalmente por API de WhatsApp" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {formatConversationDate(conv.ultimaFecha)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground truncate">{conv.ultimoMensaje}</p>
                  {conv.mensajesNoLeidos > 0 && (
                    <Badge variant="default" className="rounded-full ml-2 h-5 min-w-[20px] flex items-center justify-center">
                      {conv.mensajesNoLeidos}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </ScrollArea>
    </div>
  );
};
